import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { PlayerProfileConcise } from "./PlayerProfileConcise";
import { PlayerLastSessionCard } from "./PlayerLastSessionCard";
import { ProfileSmartTab } from "./ProfileSmartTab";
import { profile } from "./funtionsBD/Mainservice";

/**
 * Coach training view: player snapshot (profile-style + last session auto-eval) from club-scoped APIs.
 */
export function PlayerTrainingProfileDialog({ open, onClose, userId, playerId }) {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setUser(null);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const opts = {};
        if (userId) opts.view_user_id = userId;
        else if (playerId) opts.view_player_id = playerId;
        const res = await profile(opts);
        const data = res?.data?.data;
        if (!cancelled) {
          if (data && typeof data === "object" && !Array.isArray(data)) setUser(data);
          else {
            setUser(null);
            setError("not_found");
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || String(e));
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, userId, playerId]);

  const subjectUserId = user?.user_id || userId || null;
  const subjectPlayerId = !subjectUserId ? playerId : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: "min(90vh, 840px)",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
          py: 1.5,
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" component="span" fontWeight={700}>
          {t("playerProfile") || "Player profile"}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: 0,
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          bgcolor: "background.default",
        }}
      >
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={36} />
          </Box>
        )}
        {!loading && error && error !== "not_found" && (
          <Typography color="error" sx={{ p: 2 }}>
            {t("errorLoadingProfile") || error}
          </Typography>
        )}
        {!loading && error === "not_found" && (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            {t("user_not_found") || "Player not found"}
          </Typography>
        )}
        {!loading && user && (
          <Box
            sx={{
              px: { xs: 1.25, sm: 2 },
              py: { xs: 1.25, sm: 1.5 },
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <PlayerProfileConcise user={user} />
            <PlayerLastSessionCard
              viewUserId={subjectUserId || undefined}
              viewPlayerId={subjectPlayerId || undefined}
              embedded
            />
            {subjectUserId ? (
              <Box sx={{ mt: 0.5, width: "100%" }}>
                <ProfileSmartTab userId={subjectUserId} />
              </Box>
            ) : null}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
